import React from 'react';

import { InfoTooltip } from '@neptune/shared/common-ui';
import { bemBlock, Emblem, Icon, IconProps, Layout } from '@neptune/shared/venus-ui';

import { FailedIconTooltipText } from './FailedIconTooltipText';

import './ExperimentDiagnostics.less';

const block = bemBlock('experiment-diagnostics');

type ExperimentDiagnosticsProps = {
  text?: string;
  className?: string;
  layout?: 'compact';
  isRunning?: boolean;
  state?: 'running' | 'warning' | 'failed';
} & Pick<IconProps, 'size'>;

export const ExperimentDiagnostics = React.memo(function ExperimentDiagnostics({
  text,
  state,
  layout,
  isRunning,
  className,
}: ExperimentDiagnosticsProps) {
  if (layout === 'compact') {
    let icon = null;

    switch (state) {
      case 'failed':
        icon = (
          <InfoTooltip text={FailedIconTooltipText}>
            <Icon glyph="exclamation-circle" color="error" data-failed />
          </InfoTooltip>
        );
        break;
      case 'warning':
        icon = (
          <InfoTooltip text={text}>
            <Icon glyph="exclamation-triangle" color="warning" data-warning />
          </InfoTooltip>
        );

        break;
      default:
        break;
    }

    return (
      <Layout.Row
        className={block({ extra: className })}
        data-role="state-icon"
        spacedChildren="xs"
        alignItems="center"
        span="shrink"
        inline
      >
        {icon}
        {isRunning && (
          <Icon
            className={block({ element: 'running-icon', modifiers: 'running' })}
            glyph="circle-notch"
            data-running
            spin
          />
        )}
      </Layout.Row>
    );
  }

  let emblem = null;

  switch (true) {
    case state === 'failed':
      emblem = (
        <Emblem
          startDetail={<Icon color="error" glyph="exclamation-circle" size="xs" data-failed />}
          color="error"
          text="Failed"
        />
      );
      break;
    case state === 'warning':
      emblem = (
        <InfoTooltip text={text}>
          <Emblem
            color="warning"
            text={isRunning ? 'Running' : 'Warning'}
            startDetail={
              <Icon size="xs" data-warning color="warning" glyph="exclamation-triangle" />
            }
          />
        </InfoTooltip>
      );

      break;
    case isRunning:
      emblem = (
        <Emblem
          color="info"
          text="Running"
          startDetail={<Icon color="info" glyph="circle-notch" spin size="xs" data-running />}
        />
      );
      break;
    default:
      break;
  }

  if (!emblem) {
    return null;
  }

  return (
    <Layout.Row span="shrink" data-role="state-emblem">
      {emblem}
    </Layout.Row>
  );
});
